import { useMemo, type JSX } from "react";
import Bank from "@repo/icons/default/Bank";
import Chart from "@repo/icons/default/Chart";
import CreditCard from "@repo/icons/default/CreditCard";
import Dolar from "@repo/icons/default/Dolar";
import Left from "@repo/icons/default/Left";
import ReceiptMoney from "@repo/icons/default/ReceiptMoney";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { MenuList, type MenuListItem } from "@/components/ui/layout/menu/menu-list";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { RecipientEmptyStateWrapper } from "@/components/payment/bank/account/recipient-empty-state";
import { RecipientInvalidAlertWrapper } from "@/components/payment/bank/account/recipient-invalid-alert-wrapper";

const paymentsSearchParamsSchema = z.object({
  from: z.string().optional(),
});

const ManagePaymentsPage = (): JSX.Element => {
  const { flags } = Route.useRouteContext();
  const navigate = useNavigate();
  const { from } = useSearch({ from: "/settings/payments/" });

  const buildMenuItems = useMemo((): MenuListItem[] => {
    const menuItems: MenuListItem[] = [
      {
        name: "Gráficos de evolução",
        Icon: Chart,
        route: "/settings/payments/evolution-charts",
        description: "Acompanhe os pagamentos dos pacientes e seus recebimentos.",
      },
      {
        name: "Criar pagamento avulso",
        Icon: CreditCard,
        route: "/settings/payments/create",
        description:
          "Crie links de pagamentos para cobrar de seus pacientes serviços realizados fora da Livance.",
      },
      {
        name: "Lista de pagamentos",
        Icon: Dolar,
        route: "/settings/payments/patient-payments",
        description:
          "Consulte os detalhes dos pagamentos dos pacientes e acompanhe a liberação dos seus recebíveis, com valores líquidos e status atualizado.",
      },
      {
        name: "Recebimentos",
        Icon: ReceiptMoney,
        route: "/settings/payments/payables",
        description: "Acompanhe detalhes dos seus recebíveis.",
      },
      {
        name: "Conta de recebimento",
        Icon: Bank,
        route: "/settings/payments/bank-account",
        description:
          "Configure sua conta corrente para recebimentos e antecipação automática para pagamentos no crédito.",
      },
    ];

    const menu = [...menuItems];

    if (flags["remove-lista-de-recebimentos-de-gerenciar-pagamentos"]) {
      return menu.filter((item) => item.name !== "Recebimentos");
    }

    return menu;
  }, [flags]);

  return (
    <>
      <HeaderRoot>
        <HeaderButton
          icon={Left}
          align="start"
          onClick={
            from === "create-recipient"
              ? () => void navigate({ to: "/settings" })
              : undefined
          }
        />
        <HeaderTitle title="Livance Pay" align="center" />
      </HeaderRoot>
      <Page className="pt-2">
        <RecipientEmptyStateWrapper>
          <RecipientInvalidAlertWrapper>
            <MenuList menuItems={buildMenuItems} />
          </RecipientInvalidAlertWrapper>
        </RecipientEmptyStateWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/")({
  component: ManagePaymentsPage,
  validateSearch: (search) => paymentsSearchParamsSchema.parse(search),
});
